import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { Provider } from './components/ui/provider';
import './index.css';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { toaster } from './components/ui/toaster';

// バージョン情報をpackage.jsonから取得
const APP_VERSION = process.env.REACT_APP_VERSION || require('../package.json').version;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

async function enableMocking() {
  // モック環境が必要な場合のみモックを有効化
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    const { worker } = await import('./mocks/browser');
    await worker.start({
      onUnhandledRequest: 'warn',
    });

    // 開発環境でテスト用QRコードを表示
    if (process.env.NODE_ENV === 'development') {
      const { logTestQRCodes } = await import('./mocks/qrSamples');
      logTestQRCodes();
    }
  }

  // Service Worker の登録
  serviceWorkerRegistration.register({
    onUpdate: registration => {
      console.log('[App] onUpdate called');
      // 更新があれば待機中のService Workerを有効化
      if (registration.waiting) {
        console.log('[App] Sending SKIP_WAITING message');
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      } else {
        console.log('[App] No waiting worker found');
      }
    },
    onSuccess: () => {
      // 初回インストール時のみ通知
      const isFirstInstall = !localStorage.getItem('pwa-installed');
      if (isFirstInstall) {
        setTimeout(() => {
          toaster.create({
            title: 'オフライン対応が有効になりました',
            description: 'インターネット接続がなくてもアプリを利用できます',
            type: 'success',
            duration: 3000,
          });
        }, 1000);
        localStorage.setItem('pwa-installed', 'true');
      }
    },
  });

  // アプリ更新後の通知
  if (sessionStorage.getItem('pwa-updated') === 'true') {
    sessionStorage.removeItem('pwa-updated');
    setTimeout(() => {
      toaster.create({
        title: 'アプリを更新しました',
        description: `最新バージョン (v${APP_VERSION}) をお楽しみください`,
        type: 'success',
        duration: 3000,
      });
    }, 500);
  }
}

enableMocking().then(() => {
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <Provider>
          <Router>
            <App />
          </Router>
        </Provider>
      </QueryClientProvider>
    </React.StrictMode>
  );

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals();
});
